/**
 * Terminal im Heim-PC: Netzwerkbefehle, die den aktuellen Spielstand lesen.
 *
 * Bewusst nur lesend. Wer `show ...` tippt, bekommt dieselben Angaben wie auf
 * einem echten Router, ohne dass etwas verändert wird. Konfigurationsbefehle
 * antworten mit einem Hinweis: Änderungen laufen über die Oberfläche, bis die
 * Befehlszeile auch schreiben darf.
 */
import { useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { probeDestination, type Command, type World } from '@hosting-sim/core';
import { useGame } from '../../store/game';

const PEER_RELATIONS = ['customer', 'peer', 'transit', 'ibgp'];

interface Line {
  kind: 'in' | 'out' | 'err';
  text: string;
}

const HELP = [
  'Verfügbare Befehle:',
  '  show ip route        Statische Routen und angekündigte Netze je Router',
  '  show bgp summary     BGP-Sitzungen mit Zustand, Prefixen und Flaps',
  '  show interfaces      Schnittstellen der Router mit Geschwindigkeit und Link',
  '  show servers         Server mit Zustand und Rack',
  '  ping                 Ziele im Internet auflisten',
  '  ping <präfix>        Latenz und Verlust zu einem Ziel messen',
  '  traceroute           (noch nicht verfügbar)',
  '  hostname             Name des Unternehmens',
  '  clear                Bildschirm leeren',
  '  help                 Diese Übersicht',
  'Konfigurieren ist in dieser Version nur über die Oberfläche möglich.',
];

function routerLines(world: World): string[] {
  return Object.values(world.routers).flatMap((r) => {
    const out = [`${r.name}  (AS${r.bgp.localAsn}, Router-ID ${r.bgp.routerId})`];
    if (r.staticRoutes.length === 0 && r.bgp.originatedPrefixes.length === 0) {
      out.push('  keine Routen');
    }
    for (const route of r.staticRoutes) {
      out.push(`  S   ${route.prefix} via ${route.nextHop} [${route.distance}]${route.description ? `  ${route.description}` : ''}`);
    }
    for (const prefix of r.bgp.originatedPrefixes) {
      out.push(`  B   ${prefix} (angekündigt)`);
    }
    return out;
  });
}

function bgpLines(world: World): string[] {
  const sessions = Object.values(world.sessions);
  if (sessions.length === 0) return ['Keine BGP-Sitzungen.'];
  const header = 'Router           Nachbar          AS     Zustand        Prefixe rx/tx  Flaps';
  const rows = sessions.map((s) => {
    const router = world.routers[s.routerId];
    const neighbor = router?.bgp.neighbors.find((n) => n.id === s.peerId);
    const name = (router?.name ?? '?').padEnd(16);
    const address = (neighbor?.address ?? '?').padEnd(16);
    const asn = String(s.remoteAsn).padEnd(6);
    const state = s.state.padEnd(14);
    const prefixes = `${s.prefixesReceived}/${s.prefixesSent}`.padEnd(14);
    return `${name} ${address} ${asn} ${state} ${prefixes} ${s.flaps}`;
  });
  return [header, ...rows];
}

function interfaceLines(world: World): string[] {
  const routers = Object.values(world.routers);
  if (routers.length === 0) return ['Keine Router vorhanden.'];
  return routers.flatMap((r) => [
    `${r.name}`,
    ...r.ports.map((p) => {
      const link = p.linkId ? `Link ${p.linkId}` : 'nicht verbunden';
      const state = p.enabled ? 'up' : 'administrativ aus';
      const addr = p.ipv4 ?? '–';
      return `  Gi0/${p.index}  ${String(p.speedMbps).padStart(6)} Mbit/s  ${state.padEnd(16)} ${addr.padEnd(16)} ${link}${p.description ? `  ${p.description}` : ''}`;
    }),
  ]);
}

function serverLines(world: World): string[] {
  const servers = Object.values(world.servers);
  if (servers.length === 0) return ['Keine Server vorhanden.'];
  return servers.map((s) => {
    const rack = s.rackId ? world.racks[s.rackId]?.name ?? '?' : '–';
    return `${s.name.padEnd(16)} ${s.state.padEnd(12)} Rack ${rack}  Rolle ${s.role}`;
  });
}

/** Führt einen Befehl aus und liefert die Ausgabezeilen. Reine Funktion ohne Seiteneffekte. */
export function runCommand(world: World, raw: string): { lines: string[]; clear?: boolean } {
  const cmd = raw.trim().replace(/\s+/g, ' ').toLowerCase();
  if (cmd === '') return { lines: [] };
  if (cmd === 'help' || cmd === '?') return { lines: HELP };
  if (cmd === 'clear') return { lines: [], clear: true };
  if (cmd === 'hostname') return { lines: [world.company.name] };
  if (cmd === 'show ip route') return { lines: routerLines(world) };
  if (cmd === 'show bgp summary') return { lines: bgpLines(world) };
  if (cmd === 'show interfaces') return { lines: interfaceLines(world) };
  if (cmd === 'show servers') return { lines: serverLines(world) };
  if (cmd === 'ping') {
    const destinations = world.market.destinations;
    if (destinations.length === 0) return { lines: ['Keine Ziele bekannt.'] };
    return {
      lines: [
        'Ziel               Stadt            AS',
        ...destinations.map((d) => `${d.prefix.padEnd(18)} ${d.city.padEnd(16)} ${d.ownerAsn}`),
      ],
    };
  }
  if (cmd.startsWith('ping ')) {
    const prefix = cmd.slice(5).trim();
    const destination = world.market.destinations.find((d) => d.prefix.toLowerCase() === prefix);
    if (!destination) return { lines: [`% Ziel ${prefix} unbekannt. 'ping' listet die Ziele.`] };
    const result = probeDestination(world, world.derived.routingTables, destination);
    if (!result.reachable) return { lines: [`PING ${destination.prefix}: keine Route, Ziel nicht erreichbar.`] };
    return {
      lines: [
        `PING ${destination.prefix} (${destination.city}, AS${destination.ownerAsn}):`,
        `  Latenz ${result.latencyMs.toFixed(1)} ms, Verlust ${result.lossPercent.toFixed(2)} %`,
      ],
    };
  }
  if (cmd === 'traceroute' || cmd.startsWith('traceroute ')) {
    return { lines: ['% traceroute ist in dieser Version noch nicht verfügbar.'] };
  }
  if (cmd.startsWith('configure') || cmd.startsWith('conf ') || cmd === 'conf') {
    return { lines: ['% Konfigurieren ist in dieser Version nur über die Oberfläche möglich.'] };
  }
  return { lines: [`% Unbekannter Befehl: ${raw.trim()}  (help zeigt die verfügbaren Befehle)`] };
}

export interface ConfigOutcome {
  lines: string[];
  /** Kernbefehl, den die Zeile auslöst. Die Antwort kommt erst danach. */
  action?: Command;
  /** Neuer Routerkontext; `null` verlässt ihn. Fehlt das Feld, bleibt er unverändert. */
  ctx?: string | null;
  exit?: boolean;
}

/**
 * Eine Zeile im Konfigurationsmodus. Jede Änderung ist ein bestehender Kernbefehl,
 * der dort geprüft wird – hier steht nur die Übersetzung von der Befehlszeile.
 */
export function configLine(world: World, ctx: string | null, raw: string): ConfigOutcome {
  const text = raw.trim().replace(/\s+/g, ' ');
  const words = text.split(' ').filter(Boolean);
  const lower = words.map((w) => w.toLowerCase());
  const fail = (message: string): ConfigOutcome => ({ lines: [message] });

  if (text === '') return { lines: [] };
  if (lower[0] === 'end' || lower[0] === 'exit') return { lines: [], exit: true };
  if (lower[0] === '?' || lower[0] === 'help') {
    return {
      lines: [
        'Konfigurationsbefehle:',
        '  router <name>                          Router wählen',
        '  neighbor <adresse> remote-as <asn> [peer|customer|transit|ibgp] [text]',
        '  no neighbor <adresse>                  Nachbar entfernen',
        '  network <präfix> / no network <präfix> Netz ankündigen / zurückziehen',
        '  ip route <präfix> <nexthop> [distanz]  statische Route setzen',
        '  no ip route <präfix>                   statische Route entfernen',
        '  show running-config                    Konfiguration des Routers zeigen',
        '  end                                    zurück zur Ansicht',
      ],
    };
  }
  if (lower.join(' ') === 'write' || lower.join(' ') === 'write memory') {
    return { lines: ['Änderungen wirken sofort, Speichern ist nicht nötig.'] };
  }
  if (lower[0] === 'router' && words.length >= 2) {
    const name = words.slice(1).join(' ').toLowerCase();
    const router = Object.values(world.routers).find((r) => r.name.toLowerCase() === name);
    if (!router) return fail(`% Router ${words.slice(1).join(' ')} unbekannt.`);
    return { lines: [`Kontext: ${router.name}`], ctx: router.id };
  }
  if (!ctx) return fail("% Erst 'router <name>' wählen.");
  const router = world.routers[ctx as never];
  if (!router) return { lines: ['% Router existiert nicht mehr.'], ctx: null };
  if (lower.join(' ') === 'show running-config') {
    return {
      lines: [
        `! ${router.name}, AS${router.bgp.localAsn}, Router-ID ${router.bgp.routerId}`,
        ...router.bgp.neighbors.map((n) => `neighbor ${n.address} remote-as ${n.remoteAsn} ${n.relation} ${n.description}`),
        ...router.bgp.originatedPrefixes.map((p) => `network ${p}`),
        ...router.staticRoutes.map((r) => `ip route ${r.prefix} ${r.nextHop} ${r.distance}`),
        '! Ende',
      ],
    };
  }

  // neighbor <adresse> remote-as <asn> [peer|customer|transit|ibgp] [beschreibung]
  if (lower[0] === 'neighbor' && lower[2] === 'remote-as' && words.length >= 4) {
    const address = words[1];
    const asn = Number(words[3]);
    if (!Number.isInteger(asn) || asn <= 0) return fail('% Ungültige AS-Nummer.');
    const relation = (lower[4] ?? 'peer') as string;
    if (!PEER_RELATIONS.includes(relation)) return fail(`% Beziehung ${relation} unbekannt (customer, peer, transit, ibgp).`);
    const description = words.slice(5).join(' ') || undefined;
    return {
      lines: [`Nachbar ${address} (AS${asn}) wird angelegt.`],
      action: { type: 'bgp/addNeighbor', routerId: router.id, neighbor: { address, remoteAsn: asn, relation: relation as never, description } },
    };
  }
  if (lower[0] === 'no' && lower[1] === 'neighbor' && words.length >= 3) {
    const neighbor = router.bgp.neighbors.find((n) => n.address === words[2]);
    if (!neighbor) return fail(`% Nachbar ${words[2]} unbekannt.`);
    return { lines: [`Nachbar ${words[2]} wird entfernt.`], action: { type: 'bgp/removeNeighbor', routerId: router.id, peerId: neighbor.id } };
  }
  if (lower[0] === 'network' && words.length === 2) {
    return { lines: [`Netz ${words[1]} wird angekündigt.`], action: { type: 'bgp/announce', routerId: router.id, prefix: words[1] } };
  }
  if (lower[0] === 'no' && lower[1] === 'network' && words.length === 3) {
    return { lines: [`Ankündigung ${words[2]} wird zurückgezogen.`], action: { type: 'bgp/withdraw', routerId: router.id, prefix: words[2] } };
  }
  // ip route <präfix> <nexthop> [distanz]
  if (lower[0] === 'ip' && lower[1] === 'route' && words.length >= 4) {
    const distance = words[4] !== undefined ? Number(words[4]) : undefined;
    return {
      lines: [`Statische Route ${words[2]} via ${words[3]} wird gesetzt.`],
      action: { type: 'router/addStaticRoute', routerId: router.id, prefix: words[2], nextHop: words[3], distance },
    };
  }
  if (lower[0] === 'no' && lower[1] === 'ip' && lower[2] === 'route' && words.length === 4) {
    return { lines: [`Statische Route ${words[3]} wird entfernt.`], action: { type: 'router/removeStaticRoute', routerId: router.id, prefix: words[3] } };
  }
  return fail(`% Unbekannter Konfigurationsbefehl: ${text}`);
}

export function Terminal() {
  const world = useGame((s) => s.world);
  const company = world?.company.name ?? 'router';
  const [lines, setLines] = useState<Line[]>([
    { kind: 'out', text: `${company} – Netzwerkterminal (nur lesend)` },
    { kind: 'out', text: "Tippe 'help' für eine Übersicht." },
  ]);
  const [input, setInput] = useState('');
  const [configMode, setConfigMode] = useState(false);
  const [ctx, setCtx] = useState<string | null>(null);
  const run = useGame((s) => s.run);
  const history = useRef<string[]>([]);
  const historyAt = useRef(-1);
  const bottom = useRef<HTMLDivElement>(null);

  const host = useMemo(() => company.toLowerCase().replace(/\s+/g, '-'), [company]);
  const prompt = configMode ? `${host}(config${ctx && world?.routers[ctx as never] ? `-router ${world.routers[ctx as never].name}` : ''})#` : `${host}#`;

  if (!world) return null;

  const append = (newLines: Line[]) => setLines((prev) => [...prev, ...newLines]);
  const asLines = (texts: string[]): Line[] => texts.map((t) => ({ kind: (t.startsWith('%') ? 'err' : 'out') as Line['kind'], text: t }));

  const submit = async () => {
    const text = input;
    const shownPrompt = prompt;
    setInput('');
    historyAt.current = -1;
    if (text.trim()) history.current = [text, ...history.current].slice(0, 50);
    append([{ kind: 'in', text: `${shownPrompt} ${text}` }]);

    if (configMode) {
      const outcome = configLine(world, ctx, text);
      if (outcome.exit) {
        setConfigMode(false);
        setCtx(null);
      } else if (outcome.ctx !== undefined) {
        setCtx(outcome.ctx);
      }
      if (outcome.action) {
        const result = await run(outcome.action);
        append(asLines([result.ok ? outcome.lines[0] ?? result.message : `% ${result.message}`]));
      } else {
        append(asLines(outcome.lines));
      }
    } else if (/^(configure terminal|configure|conf t|conf)$/i.test(text.trim())) {
      setConfigMode(true);
      append(asLines(['Konfiguration über das Terminal. Mit "end" zurück zur Ansicht.']));
    } else {
      const result = runCommand(world, text);
      if (result.clear) setLines([]);
      else append(asLines(result.lines));
    }
    requestAnimationFrame(() => bottom.current?.scrollIntoView({ block: 'end' }));
  };

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void submit();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (historyAt.current < history.current.length - 1) {
        historyAt.current += 1;
        setInput(history.current[historyAt.current] ?? '');
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      historyAt.current = Math.max(-1, historyAt.current - 1);
      setInput(historyAt.current >= 0 ? history.current[historyAt.current] : '');
    }
  };

  return (
    <div className="rounded-lg bg-black/90 p-3 font-mono text-xs leading-relaxed text-emerald-300">
      <div className="max-h-[360px] overflow-y-auto whitespace-pre">
        {lines.map((l, i) => (
          <div key={i} className={l.kind === 'err' ? 'text-rose-400' : l.kind === 'in' ? 'text-sky-300' : undefined}>
            {l.text}
          </div>
        ))}
        <div ref={bottom} />
      </div>
      <div className="mt-2 flex items-center gap-2">
        <span className="text-sky-300">{prompt}</span>
        <input
          autoFocus
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKey}
          spellCheck={false}
          aria-label="Befehl eingeben"
          className="flex-1 bg-transparent text-emerald-200 outline-none"
        />
      </div>
    </div>
  );
}
